import React from 'react';
import { Battery, Zap, Shield, Globe, Users, Target, Rocket, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import Counter from '../components/Counter';

const AboutPage = () => {
  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="bg-slate-50 py-24 text-slate-900">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-sm font-black text-slate-900 uppercase tracking-[0.3em] mb-6">Om os</h1>
            <h2 className="text-6xl font-black mb-8 leading-[1.1] uppercase tracking-tighter">
              Vi skaber fremtidens <span className="text-slate-900">energiløsninger</span>
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed font-medium">
              GMA er en førende leverandør af intelligente energilagringsløsninger. Vi kombinerer global ekspertise med lokal indsigt for at accelerere den grønne omstilling.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://assets.auxodev.dk/assets/f4225015-9d61-46d9-9e4c-356d5fd3c807/ba451ae2-b04c-4ed3-85ce-a8edf6177646.jpg" 
                alt="Green energy" 
                className="w-full aspect-[4/3] object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-4xl font-black text-slate-900 mb-8 uppercase tracking-tighter">Vores Mission</h3>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Hos GMA er vores mission klar: Vi vil være den foretrukne partner for virksomheder og organisationer, der ønsker at optimere deres energiforbrug og bidrage til en bæredygtig fremtid.
              </p>
              <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                Vi tror på, at nøglen til en grønnere verden ligger i intelligent energilagring. Ved at kombinere avanceret batteriteknologi med smarte styresystemer, gør vi det muligt at udnytte vedvarende energi mere effektivt end nogensinde før.
              </p>
              
              <div className="grid grid-cols-2 gap-8">
                <div className="border-l-4 border-slate-900 pl-6">
                  <div className="text-4xl font-black text-slate-900 mb-1">
                    <Counter value={20} suffix="+" />
                  </div>
                  <div className="text-sm text-slate-500 font-bold uppercase tracking-wider">Års Erfaring</div>
                </div>
                <div className="border-l-4 border-slate-900 pl-6">
                  <div className="text-4xl font-black text-slate-900 mb-1">
                    <Counter value={100} suffix="%" />
                  </div>
                  <div className="text-sm text-slate-500 font-bold uppercase tracking-wider">Grøn Energi</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h3 className="text-4xl font-black text-slate-900 mb-4 uppercase tracking-tighter">Vores Værdier</h3>
            <p className="text-slate-600 max-w-2xl mx-auto">Vi bygger vores forretning på fire fundamentale principper, der sikrer kvalitet og tillid i alt hvad vi gør.</p>
          </motion.div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Target, title: "Innovation", desc: "Vi stræber altid efter de nyeste og mest effektive teknologier." },
              { icon: Users, title: "Partnerskab", desc: "Vi arbejder tæt sammen med vores kunder for at skabe værdi." },
              { icon: Award, title: "Kvalitet", desc: "Vi går aldrig på kompromis med driftssikkerhed og performance." },
              { icon: Rocket, title: "Fremtid", desc: "Vi kigger altid fremad mod en mere bæredygtig verden." }
            ].map((value, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-8 border border-slate-100 text-center"
              >
                <div className="flex items-center justify-center mx-auto mb-6 text-slate-900">
                  <value.icon size={40} />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3 uppercase">{value.title}</h4>
                <p className="text-slate-500 text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section (Optional teaser) */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl font-black text-slate-900 mb-8 uppercase tracking-tighter">Klar til at tage næste skridt?</h3>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">Kontakt os i dag for en uforpligtende samtale om hvordan vi kan hjælpe din virksomhed med intelligent energilagring.</p>
            <a href="/#contact" className="inline-block bg-slate-900 text-white px-10 py-4 font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors">
              Kontakt os nu
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
